import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useEffect, useRef} from 'react'
import {Util} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {UiSlot} from '@api/components'
import {TrackingEvent} from '@common/tracking-events'
import {getTextRightPanelWidth} from '@common/tracking-utils'
import {multiActionsWithTasksStore} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {cn} from '@containers/html-utils'
import {PageService} from '@extensions/page-service'
import {Tracking} from '../../../../common/tracking'
import {EventBus} from '../../../client/event-bus'
import {resizeChatEvent} from '../../../client/events'
import {ChatUtils} from '../../../extensions/chat-utils'
import {SVGPreload} from '../../common/svg-preload/svg-preload'
import {Transition} from '../../common/transition/transition'
import {GroupChatEdit} from '../group-chat-edit/group-chat'
import {PanelResizer} from '../panel-resizer/panel-resizer'
import {RightPanelContent} from '../right-panel-content/right-panel-content'
import {TaskChatList} from '../task-chat-list/task-chat-list'
import {useBannerTimer} from '../task-chat-list/task-chat-list-content'
import {MinimizeButton} from './minimize-button'
import {rightPanelStore} from './right-panel-store'

type PanelKind = 'personal_chats' | 'task_chats' | 'news' | 'empty'

const lastChatIdKey = 'last-chat-id'
const marginWidth = 42

interface Props {
  activePanel: PanelKind
  chatId: DataObjectId | undefined
}

export const RightPanelBase = observer((props: Props) => {
  useBannerTimer() // delete after MOB-2183
  const {chatId} = props
  const refElement = useRef<HTMLDivElement>(null)

  const onResize = (width: number) => {
    if (!refElement.current) {
      return
    }
    refElement.current.style.width = width + 'px'
  }
  const {groupChatEdit, opened} = rightPanelStore.state.chatPanel
  const minimized = !opened

  useEffect(() => {
    const lastIdTask = getLastChatId()
    const idTask = PageService.getHash().split('#')[1]

    if (idTask && idTask !== lastIdTask && minimized) {
      rightPanelStore.chatPanelToggle()
    }
  }, [])

  useEffect(() => {
    const lastIdTask = getLastChatId()
    const idTask = PageService.getHash().split('#')[1]

    if (idTask === lastIdTask) {
      return
    }

    if (idTask) {
      window.localStorage.setItem(lastChatIdKey, idTask)
    } else {
      window.localStorage.removeItem(lastChatIdKey)
    }

    if (hub?.id) {
      rightPanelStore.updateTabOnProjectChange()
    }
  }, [chatId])

  const hub = chatId && ChatUtils.findChatForCurrentUser(chatId)

  const width = getRightPanelWidth()
  const style = {width: width + 'px'}
  const bem = Util.bemFrom('chat-list-panel')

  return (
    <div
      className={cn(
        bem({minimized}),
        minimized && 'chat-list-panel-minimized',
        'border-solid',
        'border-0',
        'border-l-1',
        'border-grey-l60',
        'z-50'
      )}
      style={style}
      ref={refElement}
      onClick={() => multiActionsWithTasksStore.visibleOff()}
    >
      <div className="relative h-full">
        <SVGPreload img={['chat/maximize', 'chat/minimize']} />

        {!minimized && (
          <PanelResizer
            initialWidth={width}
            minWidth={440}
            maxWidth={800}
            onStartResize={rightPanelStore.chatPanelSetResize}
            onResize={onResize}
            onEndResize={onEndResize}
            dragAllowed={!minimized}
          />
        )}
        <style>
          {/* For better css transition performance */}
          {`
            .chat-list-panel-minimized {
              transform: translate(${width}px, 0);
            }
            .chat-list-panel__chat-container__slide--active {
              transform: translate(-${width}px, 0);
            }
            .chat-list-panel__pages__chats-page {
              transform: translate(-${Math.round(width / 2)}px, 0);
            }
            .chat-list-panel__pages__add-contact-page--active,
            .chat-list-panel__pages__edit-users-page--active {
              transform: translate(-${width}px, 0);
            }
          `}
        </style>

        <MinimizeButton
          minimized={minimized}
          onClick={rightPanelStore.chatPanelToggle}
        />

        <Transition
          input={hub}
          className={cn(bem('chat-container'), 'z-50')}
          time={300}
        >
          {savedHub => (
            <div className={bem('chat-container', 'slide', {active: !!hub})}>
              <RightPanelContent hub={savedHub} />
            </div>
          )}
        </Transition>

        {groupChatEdit && <GroupChatEdit />}

        <div className={cn(bem('pages'), 'bg-grey-l100')}>
          <div
            className={bem('pages', 'chats-page', {
              active: !hub && !groupChatEdit
            })}
          >
            <TaskChatList />
          </div>
        </div>

        <UiSlot name="before-right-panel" />
      </div>
    </div>
  )
})

const getLastChatId = (): DataObjectId | undefined => {
  type T = DataObjectId | undefined
  return (window.localStorage.getItem(lastChatIdKey) ?? undefined) as T
}

export const getRightPanelWidth = (): number => {
  const width = window.innerWidth > 0 ? window.innerWidth : screen.width
  const {chatPanel} = rightPanelStore.state
  const panelWidth =
    width < 500 || width < chatPanel.width + marginWidth
      ? width - marginWidth
      : chatPanel.width

  return panelWidth
}

const onEndResize = (width: number) => {
  rightPanelStore.chatPanelSetWidth(width)
  rightPanelStore.chatPanelSetResize(false)

  EventBus.dispatch(resizeChatEvent())
  const textWidth = getTextRightPanelWidth(width)
  void Tracking.send(TrackingEvent.rightPanelResized, {width: textWidth})
  void Tracking.sendUserProperty({
    rightPanelWidthResize: textWidth
  })
}
